import { defineMessages } from 'react-intl';

import type { AgentPromptPreset, AgentPromptPresetList } from '../config';

// Preset sets for consultation, dental photo, patient document, encounter,
// and template-editor pages.
export const workflowMessages = defineMessages({
  consultSummaryTitle: {
    id: 'agentPrompts.consultation.summaryTitle',
    defaultMessage: 'Consultation summary',
  },
  consultSummaryPrompt: {
    id: 'agentPrompts.consultation.summaryPrompt',
    defaultMessage:
      'Summarize this consultation.\n\nOutput:\n1. Chief complaint and reason for visit\n2. What was discussed and decided\n3. Key clinical findings captured in the transcript\n4. Recommendations and instructions given to the patient\n5. Open questions or items to confirm\n\nBase the summary only on the transcript and available consultation artifacts. Do not add diagnoses that were not stated. Answer in the chat; do not create a patient document.',
  },
  consultActionItemsTitle: {
    id: 'agentPrompts.consultation.actionItemsTitle',
    defaultMessage: 'Action items',
  },
  consultActionItemsPrompt: {
    id: 'agentPrompts.consultation.actionItemsPrompt',
    defaultMessage:
      'Extract the action items from this consultation.\n\nOutput:\n1. Tasks for the clinician (verify, order, decide)\n2. Tasks for assistants or coordinators (schedule, prepare, contact)\n3. Commitments made to the patient, with timing\n4. Missing decisions or data that block next steps\n\nAnswer in the chat. Do not create internal tasks and do not create a patient document.',
  },
  consultPlanDraftTitle: {
    id: 'agentPrompts.consultation.planDraftTitle',
    defaultMessage: 'Draft treatment plan',
  },
  consultPlanDraftPrompt: {
    id: 'agentPrompts.consultation.planDraftPrompt',
    defaultMessage:
      'Turn the discussion in this consultation into a structured draft treatment plan.\n\nOutput:\n1. Problem list from the transcript, with tooth numbers where stated\n2. Proposed phases: urgent items first, then disease control, then definitive care\n3. Open items that need imaging or clinical confirmation\n4. Items to discuss with the patient before consent\n\nThis is a draft for clinician review — sequencing and decisions stay with the clinician. Answer in the chat; do not create a patient document.',
  },
  consultFollowUpTitle: {
    id: 'agentPrompts.consultation.followUpTitle',
    defaultMessage: 'Patient follow-up message',
  },
  consultFollowUpDetail: {
    id: 'agentPrompts.consultation.followUpDetail',
    defaultMessage: 'Plain-language recap of the consultation',
  },
  consultFollowUpPrompt: {
    id: 'agentPrompts.consultation.followUpPrompt',
    defaultMessage:
      'Draft a patient-friendly follow-up message based on this consultation.\n\nOutput:\n1. Short summary of what was discussed\n2. Simple explanation of the next step\n3. Reassuring closing sentence\n4. Call to action to schedule or continue communication\n\nUse calm, non-technical language. Do not mention diagnoses unless they were explicitly confirmed by the clinician in the transcript. Reply with the message in the chat. Do not create a patient document.',
  },
  consultReferralTitle: {
    id: 'agentPrompts.consultation.referralTitle',
    defaultMessage: 'Referral letter',
  },
  consultReferralDetail: {
    id: 'agentPrompts.consultation.referralDetail',
    defaultMessage: 'Summary of the consultation for a specialist',
  },
  consultReferralPrompt: {
    id: 'agentPrompts.consultation.referralPrompt',
    defaultMessage:
      'Create and publish a patient document "Referral letter" based on this consultation — not just a chat answer.\n\nWrite the complete patient document Markdown body: the reason for referral with the specific clinical question, the relevant findings and history from the consultation, the supporting materials, what the specialist should evaluate, and the urgency.\n\nCreate the document with the patient-document workflow, attach the relevant study images, and publish it.\n\nReply in chat with only a concise summary of the created document. Never paste the document body into the chat.',
  },
  photosSummaryTitle: {
    id: 'agentPrompts.photos.summaryTitle',
    defaultMessage: 'Photo documentation summary',
  },
  photosSummaryPrompt: {
    id: 'agentPrompts.photos.summaryPrompt',
    defaultMessage:
      'Summarize the dental photo documentation for this patient.\n\nOutput:\n1. What photo series exist and when they were taken\n2. What the photos document clinically, area by area\n3. Observations visible in the photos that relate to the patient records\n4. Gaps in the photo documentation for this case\n\nDescribe only what is visible; do not diagnose from photos. Answer in the chat; do not create a patient document.',
  },
  photosVsReportTitle: {
    id: 'agentPrompts.photos.vsReportTitle',
    defaultMessage: 'Photos vs latest report',
  },
  photosVsReportPrompt: {
    id: 'agentPrompts.photos.vsReportPrompt',
    defaultMessage:
      'Cross-check the dental photos against the latest AI report for this patient.\n\nOutput:\n1. Where photo observations agree with the report\n2. Where they differ or the photos add information\n3. Areas where neither the photos nor the report give enough information\n4. What the clinician should look at directly\n\nAnswer in the chat; do not create a patient document.',
  },
  documentReviewTitle: {
    id: 'agentPrompts.document.reviewTitle',
    defaultMessage: 'Review this document',
  },
  documentReviewPrompt: {
    id: 'agentPrompts.document.reviewPrompt',
    defaultMessage:
      'Review this patient document before it is finalized.\n\nOutput:\n1. Accuracy: statements that conflict with the underlying records or imaging\n2. Completeness: sections a document of this type should have but lacks\n3. Clarity: confusing or ambiguous passages\n4. Consistency: dates, tooth numbers, terminology\n\nDo not rewrite the document; report the findings. Answer in the chat.',
  },
  documentSimplifyTitle: {
    id: 'agentPrompts.document.simplifyTitle',
    defaultMessage: 'Simplify for the patient',
  },
  documentSimplifyDetail: {
    id: 'agentPrompts.document.simplifyDetail',
    defaultMessage: 'Plain-language rewrite of the document',
  },
  documentSimplifyPrompt: {
    id: 'agentPrompts.document.simplifyPrompt',
    defaultMessage:
      'Rewrite this patient document in plain patient-friendly language.\n\nKeep every fact, date, and tooth reference intact. Replace clinical jargon with everyday words, shorten long sentences, and keep the document structure.\n\nApply the rewrite with the patient-document workflow and publish it. Reply in chat with only a concise summary of the changes. Never paste the full document body into the chat.',
  },
  documentCondenseTitle: {
    id: 'agentPrompts.document.condenseTitle',
    defaultMessage: 'Tighten the text',
  },
  documentCondenseDetail: {
    id: 'agentPrompts.document.condenseDetail',
    defaultMessage: 'Shorter version without losing facts',
  },
  documentCondensePrompt: {
    id: 'agentPrompts.document.condensePrompt',
    defaultMessage:
      'Tighten this patient document: remove repetition, merge overlapping sections, and shorten the text without losing any fact, date, measurement, or tooth reference.\n\nApply the rewrite with the patient-document workflow and publish it. Reply in chat with only a concise summary of the changes. Never paste the full document body into the chat.',
  },
  encounterSummaryTitle: {
    id: 'agentPrompts.encounter.summaryTitle',
    defaultMessage: 'Encounter summary',
  },
  encounterSummaryPrompt: {
    id: 'agentPrompts.encounter.summaryPrompt',
    defaultMessage:
      'Summarize this encounter.\n\nOutput:\n1. Purpose and type of the encounter\n2. What happened, in order\n3. Key decisions and instructions\n4. Open items\n\nBase the summary on the encounter data and attached documents. Answer in the chat; do not create a patient document.',
  },
  encounterCompletenessTitle: {
    id: 'agentPrompts.encounter.completenessTitle',
    defaultMessage: 'Documentation check',
  },
  encounterCompletenessPrompt: {
    id: 'agentPrompts.encounter.completenessPrompt',
    defaultMessage:
      'Check whether this encounter is fully documented.\n\nOutput:\n1. What is documented\n2. Missing notes, documents, or artifacts\n3. Incomplete or inconsistent entries (undated, unsigned, empty sections)\n4. What to add before closing the encounter\n\nFocus only on documentation completeness. Answer in the chat; do not create a patient document.',
  },
  encounterLetterTitle: {
    id: 'agentPrompts.encounter.letterTitle',
    defaultMessage: 'Patient follow-up message',
  },
  encounterLetterDetail: {
    id: 'agentPrompts.encounter.letterDetail',
    defaultMessage: 'Recap of this encounter for the patient',
  },
  encounterLetterPrompt: {
    id: 'agentPrompts.encounter.letterPrompt',
    defaultMessage:
      'Draft a follow-up message to the patient based on this encounter.\n\nOutput:\n1. Short recap of what was discussed or done\n2. Next steps in plain words\n3. Reassuring closing sentence\n4. Call to action to schedule or continue communication\n\nUse calm, non-technical language. Do not mention diagnoses unless they were explicitly confirmed by the clinician. Reply with the message in the chat. Do not create a patient document.',
  },
  templatesReviewTitle: {
    id: 'agentPrompts.templates.reviewTitle',
    defaultMessage: 'Review this template',
  },
  templatesReviewPrompt: {
    id: 'agentPrompts.templates.reviewPrompt',
    defaultMessage:
      'Review the current document template and suggest improvements.\n\nOutput:\n1. What the template covers well\n2. Missing or redundant sections for its purpose\n3. Wording or ordering improvements\n4. Concrete change suggestions\n\nBase the review on the template and its series data. Do not modify anything yet. Answer in the chat.',
  },
  templatesProposalTitle: {
    id: 'agentPrompts.templates.proposalTitle',
    defaultMessage: 'Propose a change',
  },
  templatesProposalDetail: {
    id: 'agentPrompts.templates.proposalDetail',
    defaultMessage: 'Prepare a template change proposal for approval',
  },
  templatesProposalPrompt: {
    id: 'agentPrompts.templates.proposalPrompt',
    defaultMessage:
      'Propose one concrete improvement to this document template.\n\nPrepare the change as a preview proposal with the template-change workflow: describe exactly what changes and why, and wait for approval. Do not apply anything without an approved proposal.\n\nReply in chat with a concise description of the proposed change.',
  },
});

const p = (
  id: string,
  group: AgentPromptPreset['group'],
  title: (typeof workflowMessages)[keyof typeof workflowMessages],
  prompt: typeof title,
  detail?: typeof title,
): AgentPromptPreset => ({ id, group, title, prompt, detail });

/** Transcriptor consultation page. */
export const consultationPagePresets: AgentPromptPresetList = {
  write: [
    p(
      'consultation.followUp',
      'write',
      workflowMessages.consultFollowUpTitle,
      workflowMessages.consultFollowUpPrompt,
      workflowMessages.consultFollowUpDetail,
    ),
    p(
      'consultation.referral',
      'write',
      workflowMessages.consultReferralTitle,
      workflowMessages.consultReferralPrompt,
      workflowMessages.consultReferralDetail,
    ),
  ],
  understand: [
    p(
      'consultation.summary',
      'understand',
      workflowMessages.consultSummaryTitle,
      workflowMessages.consultSummaryPrompt,
    ),
    p(
      'consultation.actionItems',
      'understand',
      workflowMessages.consultActionItemsTitle,
      workflowMessages.consultActionItemsPrompt,
    ),
    p(
      'consultation.planDraft',
      'understand',
      workflowMessages.consultPlanDraftTitle,
      workflowMessages.consultPlanDraftPrompt,
    ),
  ],
};

export const photosPagePresets: AgentPromptPresetList = {
  write: [],
  understand: [
    p(
      'photos.summary',
      'understand',
      workflowMessages.photosSummaryTitle,
      workflowMessages.photosSummaryPrompt,
    ),
    p(
      'photos.vsReport',
      'understand',
      workflowMessages.photosVsReportTitle,
      workflowMessages.photosVsReportPrompt,
    ),
  ],
};

/** Patient document editor page (document-scoped chat). */
export const documentPagePresets: AgentPromptPresetList = {
  write: [
    p(
      'document.simplify',
      'write',
      workflowMessages.documentSimplifyTitle,
      workflowMessages.documentSimplifyPrompt,
      workflowMessages.documentSimplifyDetail,
    ),
    p(
      'document.condense',
      'write',
      workflowMessages.documentCondenseTitle,
      workflowMessages.documentCondensePrompt,
      workflowMessages.documentCondenseDetail,
    ),
  ],
  understand: [
    p(
      'document.review',
      'understand',
      workflowMessages.documentReviewTitle,
      workflowMessages.documentReviewPrompt,
    ),
  ],
};

export const encounterPagePresets: AgentPromptPresetList = {
  write: [
    p(
      'encounter.letter',
      'write',
      workflowMessages.encounterLetterTitle,
      workflowMessages.encounterLetterPrompt,
      workflowMessages.encounterLetterDetail,
    ),
  ],
  understand: [
    p(
      'encounter.summary',
      'understand',
      workflowMessages.encounterSummaryTitle,
      workflowMessages.encounterSummaryPrompt,
    ),
    p(
      'encounter.completeness',
      'understand',
      workflowMessages.encounterCompletenessTitle,
      workflowMessages.encounterCompletenessPrompt,
    ),
  ],
};

/** Company settings — Transcriptor template editors. */
export const templatesPagePresets: AgentPromptPresetList = {
  write: [
    p(
      'templates.proposal',
      'write',
      workflowMessages.templatesProposalTitle,
      workflowMessages.templatesProposalPrompt,
      workflowMessages.templatesProposalDetail,
    ),
  ],
  understand: [
    p(
      'templates.review',
      'understand',
      workflowMessages.templatesReviewTitle,
      workflowMessages.templatesReviewPrompt,
    ),
  ],
};
